import React, { useCallback, useMemo, useState } from "react";
import * as Yup from "yup";
import {
  WSButton,
  WSElement,
  WSFlexBox,
  WSForm,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  Form1099Status,
  ICollaboratorSchema,
  IMemberClientForm1099Balances,
  IMemberClientFormW9Info,
  IPayrollSettings
} from "@wingspanhq/payments/dist/interfaces";

import {
  companyNameValidator,
  validatorAddressLine1,
  validatorAddressLine2,
  validatorSSN,
  validatorTaxId
} from "../../../../utils/validators";
import { getCollaboratorName } from "../../../../query/payments/selectors";
import { SidePanelSection } from "../../../../components/SidePanel";
import getDetailsAndSettingsItems from "../../../utils/getDetailsAndSettingsItems";
import {
  getPayeeInfoW9EditItems,
  getPayeeInfoOtherItems
} from "../../../utils/payee-info";
import {
  getNonEmployeeCompensation,
  nonEditableItems
} from "../../../utils/getTotalsItems";

import CombinedEligibility from "../CombinedEligibility";
import LabelValueList, {
  LABEL_COLOR,
  ListElement,
  LIST_MT
} from "../../../../components/LabelValueList";
import FormFieldList, {
  ComponentProps
} from "../../../../components/FormFieldList";
import getAvailableStatusOptions from "./getAvailableStatusOptions";
import FormField1099Status from "./FormField1099Status";
import { getCurrentFilingYear } from "../../../utils";
import { QUERY_COLLABORATOR } from "../../../../query/payments/keys";
import {
  ICollaboratorPatchRequest,
  paymentsService
} from "../../../../services/payments";
import { useWSMutation } from "../../../../query/helpers";
import { AxiosError } from "axios";
import { useFormContext } from "react-hook-form";
import {
  BUSINESS_TYPE_OPTIONS,
  getBusinessTypeLabel
} from "../../../../Settings/components/SettingsUpdateForms/BusinessTypeForm";
import { US_STATES_OPTIONS } from "../../../../constants/user";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { ErrorContextKey } from "../../../../services/platform";

type EditedValues = {
  form1099Status: Form1099Status;
  nonEployeeCompensation: number;
  emailAddress: string;
  phone: string;
} & Partial<IMemberClientFormW9Info>;

const componentPropsForMoney: ComponentProps = {
  prefix: "$",
  formatOptions: {
    numeral: true,
    numeralDecimalScale: 2,
    numeralThousandsGroupStyle: "thousand",
    rawValueTrimPrefix: true
  }
};

interface ContentEditableProps {
  collaborator: ICollaboratorSchema;
  currentForm1099Balance: IMemberClientForm1099Balances | undefined;
  payrollSettings: IPayrollSettings;
  onCancel: () => void;
}

const ContentEditable: React.FC<ContentEditableProps> = ({
  collaborator,
  currentForm1099Balance,
  payrollSettings,
  onCancel
}) => {
  const [isPayeeInfoEdited, setIsPayeeInfoEdited] = useState(false);

  const [mutate, mutateMeta] = useWSMutation<
    ICollaboratorSchema,
    AxiosError,
    ICollaboratorPatchRequest
  >(
    async requestBody =>
      paymentsService.collaborator.patch(
        collaborator.collaboratorId,
        requestBody
      ),
    {
      dependencies: [[QUERY_COLLABORATOR, collaborator.collaboratorId]],
      onSuccess: onCancel
    }
  );

  const form1099Status = currentForm1099Balance?.status;

  // TODO: isCombinedEligibility is mocked data to be replaced with actual logic
  const isCombinedEligibility = false;
  const titleAside = isCombinedEligibility ? <CombinedEligibility /> : null;

  const payeeInfoW9EditItems = useMemo(
    () => getPayeeInfoW9EditItems(collaborator),
    [collaborator]
  );

  const payeeInfoOtherEditItems = useMemo(
    () => getPayeeInfoOtherItems(collaborator),
    [collaborator]
  );

  const payeeDefaultValues = useMemo(() => {
    const payeeInfoEditItems = isPayeeInfoEdited && [
      {
        name: "companyStructure",
        value: collaborator.formW9Data?.companyStructure
      },
      ...payeeInfoW9EditItems,
      ...payeeInfoOtherEditItems
    ];

    return payeeInfoEditItems
      ? payeeInfoEditItems.reduce<{
          [key: string]: any | number | undefined;
        }>((acc, item) => {
          acc[item.name] = item.value || "";

          return acc;
        }, {})
      : {};
  }, [
    collaborator.formW9Data,
    isPayeeInfoEdited,
    payeeInfoOtherEditItems,
    payeeInfoW9EditItems
  ]);

  const availableStatusOptions = useMemo(
    () => getAvailableStatusOptions(collaborator, form1099Status),
    [collaborator, form1099Status]
  );

  const formDefaultValues = useMemo(
    () => ({
      form1099Status,
      nonEployeeCompensation:
        currentForm1099Balance &&
        getNonEmployeeCompensation(currentForm1099Balance),
      ...payeeDefaultValues
    }),
    [currentForm1099Balance, form1099Status, payeeDefaultValues]
  );

  const getAdjustments = useCallback(
    nonEployeeCompensation =>
      Number(nonEployeeCompensation) -
      (Number(currentForm1099Balance?.platformIncome) || 0) -
      (Number(currentForm1099Balance?.paymentProcessingFees) || 0),
    [
      currentForm1099Balance?.paymentProcessingFees,
      currentForm1099Balance?.platformIncome
    ]
  );

  const collaboratorExternalId = useMemo(
    () => collaborator.clientData?.externalId || "-",
    [collaborator.clientData]
  );

  const submit = useCallback(
    (formValues: EditedValues) => {
      mutateMeta.reset();

      const currentFilingYear = getCurrentFilingYear();
      const adjustments = getAdjustments(formValues.nonEployeeCompensation);
      const formW9DataEdited = isPayeeInfoEdited
        ? payeeInfoW9EditItems.reduce<Partial<IMemberClientFormW9Info>>(
            (acc, { name: propName }) => {
              if (propName === "companyStructure")
                acc[propName] = formValues[propName];
              else acc[propName] = formValues[propName];
              return acc;
            },
            { companyStructure: formValues.companyStructure }
          )
        : null;

      const submitBody = {
        form1099Balances: {
          [currentFilingYear]: {
            status:
              formValues.form1099Status !== currentForm1099Balance?.status
                ? formValues.form1099Status
                : undefined,
            adjustments:
              adjustments !== currentForm1099Balance?.adjustments
                ? adjustments
                : undefined
          }
        },
        formW9Data: {
          ...collaborator.formW9Data,
          companyStructure: formValues.companyStructure,
          ...formW9DataEdited,
          state: formValues.state
        }
        // ready but taken out until supported by the backend
        // member: {
        //   user: {
        //     email: formValues.emailAddress,
        //     phone: formValues.phone,
        //   },
        // },
      };

      mutate(submitBody);
    },
    [
      collaborator.formW9Data,
      currentForm1099Balance?.adjustments,
      currentForm1099Balance?.status,
      getAdjustments,
      isPayeeInfoEdited,
      mutate,
      mutateMeta,
      payeeInfoW9EditItems
    ]
  );

  const Actions: React.FC<{ marginTop?: boolean }> = ({ marginTop }) => {
    const { reset } = useFormContext();

    return (
      <>
        <WSFlexBox justify="space-between" mt={marginTop ? "M" : undefined}>
          <WSButton.Tertiary
            type="button"
            style={{ flexGrow: 1 }}
            mr="XS"
            onClick={onCancel}
          >
            Cancel
          </WSButton.Tertiary>
          <WSButton.Primary style={{ flexGrow: 1 }} ml="XS">
            Save
          </WSButton.Primary>
        </WSFlexBox>
        <WSButton.Link
          type="button"
          mt="S"
          style={{ marginLeft: "auto", marginRight: "auto" }}
          onClick={() => reset(formDefaultValues)}
        >
          Revert changes
        </WSButton.Link>
        <WSText.ParagraphSmall mt="S" color={LABEL_COLOR}>
          Reverting changes reloads the 1099-NEC information first generated by
          Wingspan.
        </WSText.ParagraphSmall>
      </>
    );
  };

  return (
    <>
      <WSForm<EditedValues>
        key={String(isPayeeInfoEdited)}
        validationSchema={Yup.object().shape({
          companyStructure: Yup.string().required("Required"),
          ssn: Yup.string().when("ein", {
            is: ein => !ein,
            then: Yup.string().when("ssn", {
              is: ssn => !ssn,
              then: Yup.string().required(
                "SSN is required if no EIN is provided"
              ),
              otherwise: validatorSSN
            })
          }),
          ein: validatorTaxId,
          legalBusinessName: Yup.string().when("ein", {
            is: ein => Boolean(ein),
            then: companyNameValidator.required("Required"),
            otherwise: companyNameValidator
          }),
          addressLine1: validatorAddressLine1,
          addressLine2: validatorAddressLine2,
          city: Yup.string().required("City is required"),
          state: Yup.string().required("Required")
        })}
        defaultValues={formDefaultValues}
        onSubmit={submit}
      >
        <SidePanelSection first>
          <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
            <WSText.Heading3 data-testid="clientName">
              {getCollaboratorName(collaborator)}
            </WSText.Heading3>
          </WSFlexBox.CenterY>
          {availableStatusOptions.length > 0 && (
            <FormField1099Status statusOptions={availableStatusOptions} />
          )}
        </SidePanelSection>

        <SidePanelSection title="Totals" titleAside={titleAside}>
          <WSText color={LABEL_COLOR} mt={LIST_MT}>
            Non-Employee Compensation
          </WSText>
          <WSForm.Field
            name="nonEployeeCompensation"
            component={WSTextInput}
            componentProps={componentPropsForMoney}
          />
          {isPayeeInfoEdited ? null : <Actions marginTop />}
          <LabelValueList items={nonEditableItems} />
        </SidePanelSection>

        <SidePanelSection
          title="Payee information"
          titleAside={
            !isPayeeInfoEdited && (
              <WSButton.Link
                onClick={() => setIsPayeeInfoEdited(!isPayeeInfoEdited)}
              >
                Edit
              </WSButton.Link>
            )
          }
        >
          {isPayeeInfoEdited && (
            <>
              <WSElement mt={LIST_MT}>
                <ListElement color={LABEL_COLOR} value="Company type" />
                <WSForm.Field
                  name="companyStructure"
                  component={WSSelect}
                  componentProps={{ options: BUSINESS_TYPE_OPTIONS }}
                />
              </WSElement>
              <FormFieldList items={payeeInfoW9EditItems} />
              <WSForm.Field
                mt="M"
                name="state"
                component={WSSelect}
                componentProps={{
                  options: US_STATES_OPTIONS,
                  placeholder: "Select",
                  searchable: true
                }}
                label="State"
              />
              <LabelValueList items={payeeInfoOtherEditItems} />
              <WSFlexBox justify="space-between" mt={LIST_MT}>
                <ListElement
                  color={LABEL_COLOR}
                  value="External Contractor Id"
                />
                <ListElement value={collaboratorExternalId} />
              </WSFlexBox>
            </>
          )}
          {!isPayeeInfoEdited && (
            <>
              <WSFlexBox justify="space-between" mt={LIST_MT}>
                <ListElement color={LABEL_COLOR} value="Company type" />
                <ListElement
                  value={
                    getBusinessTypeLabel(
                      collaborator.formW9Data?.companyStructure ||
                        collaborator.member.profile?.company?.structure
                    ) || "-"
                  }
                />
              </WSFlexBox>
              <LabelValueList items={payeeInfoW9EditItems} />
              <WSFlexBox justify="space-between" mt={LIST_MT}>
                <ListElement color={LABEL_COLOR} value="State" />
                <ListElement value={collaborator.formW9Data?.state || "-"} />
              </WSFlexBox>
              <LabelValueList items={payeeInfoOtherEditItems} />
              <WSFlexBox justify="space-between" mt={LIST_MT}>
                <ListElement
                  color={LABEL_COLOR}
                  value="External Contractor Id"
                />
                <ListElement value={collaboratorExternalId} />
              </WSFlexBox>
            </>
          )}
        </SidePanelSection>

        {!isPayeeInfoEdited && (
          <SidePanelSection title="Form details & calculation settings">
            <LabelValueList
              items={getDetailsAndSettingsItems(
                payrollSettings,
                currentForm1099Balance?.type
              )}
            />
          </SidePanelSection>
        )}

        {isPayeeInfoEdited && (
          <SidePanelSection>
            <Actions />
          </SidePanelSection>
        )}
      </WSForm>

      {!isPayeeInfoEdited && (
        <SidePanelSection title="Form details & calculation settings">
          <LabelValueList
            items={getDetailsAndSettingsItems(
              payrollSettings,
              currentForm1099Balance?.type
            )}
          />
        </SidePanelSection>
      )}

      <WSErrorMessage
        mt="M"
        contextKey={ErrorContextKey.NEC1099DetailsUpdate}
        error={mutateMeta.error}
      />
    </>
  );
};

export default ContentEditable;
